// Join with a code (LEAGUE_SETUP.md, scenarios A3 and B2): the code is
// live-validated into a one-line preview of the league, then a team name
// and favorite pro team, then you're in.
//
// OR TAKE OVER A TEAM (MANAGERS.md, J4). A code can be one team's link, which
// the commissioner sends past the draft: the preview names the team and who
// asked, the name is the team's own unless they change it, and no pro team
// keeps the team's colors — mid-season it is a team the league already knows.
//
// AGAINST THE SERVER as of 2026-09-14 (ICD-S01). It used to ask
// `sim.leagueByCode`, which is a simulated world in the browser, and that
// could not work for the thing this screen is for: the simulator has exactly
// one protagonist, so `sim.joinLeague` answered "You are already in this
// league" to a brand-new person following an invite to a league the
// protagonist happened to be in. A second real manager could not join
// anything, whoever they were.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formatInZone } from '@fsp/domain';
import { useSession } from '../data/session';
import { deviceZone, useReaderZone } from '../data/hooks';
import { useLeagues } from '../data/LeaguesProvider';
import { COMPLETE_CODE as COMPLETE, joinLeague, useInvite } from '../data/invite';
import { messageOf } from '../data/api';
import { useDelight } from '../delight/useDelight';
import { getSport } from '../sports/registry';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { Button, Choice, Line, TextField, tell } from '../kit/ui';

/** A team's link that was used or canceled: the server's sentence. */
const SPENT = 'That link no longer works. Ask whoever sent it for a new one.';

/** An invited league's draft, in the reader's zone and named: as the hub will show it (B-180, B-181). */
function when(iso: string | null, zone: string): string {
  if (iso === null) return 'draft not scheduled yet';
  return `drafts ${formatInZone(iso, zone, { weekday: 'short', hour: 'numeric', minute: '2-digit' })}`;
}

export function JoinScreen() {
  const params = useParams();
  const zone = useReaderZone() ?? deviceZone();
  const navigate = useNavigate();
  const { session } = useSession();
  const leagues = useLeagues();
  const { play } = useDelight();
  const me = session?.displayName ?? 'You';

  const [code, setCode] = useState((params.code ?? '').toUpperCase());
  // THE INVITE AN EMAILED LINK CARRIED (PLAN-2026-10-28-by-email §3.1): the
  // join ends it, whatever address this account signs in with. It confers
  // nothing; the code does.
  const [search] = useSearchParams();
  const emailed = search.get('invite');
  const { snapshot: invite, reload: reloadInvite } = useInvite(code);
  // `ready` with a value is a league; `ready` with null is a code that names
  // nothing, which is a thing to say rather than a failure. `failed` is the
  // third answer and it is the server's own sentence.
  const league = invite.state === 'ready' ? invite.value : null;
  /** The team a link hands over; null for a league's code. */
  const team = league?.team ?? null;
  // Start on the team step only once the link's code has RESOLVED. Jumping
  // straight there because a code was merely present let somebody name a
  // team for a league that does not exist, and the refusal only appeared if
  // they pressed Back.
  const [index, setIndex] = useState(0);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    // Only when the code will seat them: a used link or a league that has
    // drafted stays on the step that says so (the review of J4).
    if (!touched && params.code && league?.open) setIndex(1);
  }, [touched, params.code, league]);

  const suggestions = useMemo(() => [`${me}'s Team`, `Team ${me}`, `${me} Nation`], [me]);
  const [teamName, setTeamName] = useState(suggestions[0]!);
  // A team taken over keeps its name unless they give it another: the field
  // starts on it once the link resolves.
  const [named, setNamed] = useState(false);
  useEffect(() => {
    if (team !== null && !named) setTeamName(team.name);
  }, [team, named]);
  // NO PRO TEAM UNTIL ONE IS PICKED (SCENARIOS L2). It was Minnesota by default, so
  // every joiner who skipped the list wore purple and gold, all of them the
  // same; now a seat comes dressed in its own house pair and a joiner who
  // picks nothing wears it. Picking a team is still one tap, and the Edit team
  // sheet changes either later.
  const [pro, setPro] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  const full = league !== null && league.joined >= league.of;
  // A CO-MANAGER'S LINK (B13): a place beside the team's manager, not the
  // team; nothing to name, and the team keeps its own name and colors.
  const helping = team?.as === 'co-manager';
  const record = team?.record
    ? `, ${team.record.wins}–${team.record.losses}${team.record.ties > 0 ? `–${team.record.ties}` : ''} so far`
    : '';
  const preview =
    invite.state === 'loading'
      ? 'Looking it up…'
      : invite.state === 'failed'
        ? invite.failure.message
        : league && team
          ? league.open
            ? helping
              ? `${team.invitedBy} asked you to help run ${team.name} in ${league.leagueName}${record}.`
              : `${team.invitedBy} asked you to take over ${team.name} in ${league.leagueName}${record}. ${league.of} teams.`
            : `A link to ${team.name} in ${league.leagueName}.`
          : league
            ? `That's ${league.commissionerName}'s league: ${league.leagueName}, ${league.of} teams, ${when(league.draftAt, zone)}. ${league.joined} of ${league.of} in.`
            : COMPLETE.test(code)
              ? 'No league with that code. Check with whoever sent it.'
              : 'Eight letters and numbers, no zeros or ones.';

  const sport = league ? getSport(league.sport as never) : null;
  const colors = useMemo(() => {
    const team = sport?.proTeams.find((t) => t.id === pro);
    return team ? { primary: team.palette.primary, secondary: team.palette.secondary } : undefined;
  }, [sport, pro]);

  const codeError =
    invite.state === 'loading'
      ? 'Checking…'
      : // A lookup that failed learned nothing about the code, so it is never
        // "that code does not match a league" — which sent somebody holding a
        // good code back to whoever sent it (B-133's review, 2026-09-24).
        invite.state === 'failed'
        ? 'Could not check the code yet.'
        : league
          ? league.open
            ? full && team === null
              ? `That league is full. Ask ${league.commissionerName} to open a seat.`
              : null
            : team !== null
              ? SPENT
              : 'That league has already drafted. Ask the commissioner for a link to a team.'
          : COMPLETE.test(code)
            ? 'That code does not match a league.'
            : 'Enter the code to continue.';

  const steps: WizardStep[] = [
    {
      id: 'code',
      title: 'Got a code?',
      hint: 'It came with the invite link, or ask the commissioner to read it out.',
      content: (
        <>
          <TextField
            label="Invite code"
            hideLabel
            code
            value={code}
            onChange={(v) => {
              setTouched(true);
              setCode(v.toUpperCase().replace(/[^A-Z0-9]/g, ''));
            }}
            placeholder="KZ7M4QRT"
            maxLength={8}
            autoFocus
          />
          {invite.state === 'failed' ? (
            <Line>
              <Button size="xs" onClick={reloadInvite}>
                Try again
              </Button>
            </Line>
          ) : null}
        </>
      ),
      preview,
      error: codeError,
    },
    {
      id: 'team',
      title:
        helping && team
          ? `Help run ${team.name}`
          : team
            ? `Take over ${team.name}`
            : 'Name your team',
      // Who asked, and the record: the code step said it, and a link skips that step.
      hint:
        helping && team
          ? `${team.invitedBy} asked you to help run ${team.name} in ${league?.leagueName ?? 'the league'}${record}. You set its lineup, make moves, trade and draft as its manager does, and the team keeps its name and colors.`
          : team
            ? `${team.invitedBy} asked you to take over ${team.name} in ${league?.leagueName ?? 'the league'}${record}. It comes with its roster, its record and its colors. Keep its name or give it yours; Edit team changes the colors later.`
            : 'Your seat comes in its own colors. Pick a favorite pro team to wear theirs instead.',
      content: helping ? null : (
        <>
          <TextField
            label="Team name"
            hideLabel
            value={teamName}
            onChange={(v) => {
              setNamed(true);
              setTeamName(v);
            }}
            maxLength={40}
            autoFocus
          />
          <Line gap={1} wrap>
            {(team ? [team.name, ...suggestions] : suggestions).map((s) => (
              <Button
                key={s}
                size="xs"
                onClick={() => {
                  setNamed(true);
                  setTeamName(s);
                }}
              >
                {s}
              </Button>
            ))}
          </Line>
          {sport ? (
            <Choice
              label={`Favorite ${sport.proLeagueName} team`}
              placeholder={team ? 'None: keep the team’s colors' : "None: my seat's house colors"}
              value={pro}
              onChange={setPro}
              options={sport.proTeams.map((t) => ({ value: t.id, label: t.name }))}
              clearable
            />
          ) : null}
        </>
      ),
      doneLabel: joining
        ? helping
          ? 'Joining…'
          : team
            ? 'Taking over…'
            : 'Joining…'
        : helping
          ? 'Join the team'
          : team
            ? 'Take over'
            : 'Join',
      // A code that will not seat them is refused here too, so Take over and
      // Join are never offered for a seat the server will refuse.
      error: codeError ?? (!helping && teamName.trim().length < 2 ? 'Two letters or more.' : null),
    },
  ];

  const join = () => {
    if (joining) return;
    setJoining(true);
    void (async () => {
      // A co-manager names nothing: the server keeps the team as it is.
      const reply = await joinLeague(code, {
        teamName: helping && team ? team.name : teamName.trim(),
        colors: helping ? undefined : colors,
        ...(emailed !== null && emailed.length >= 16 ? { invite: emailed } : {}),
      });
      setJoining(false);
      if (reply.status !== 'ok') {
        // The server's wording: "That league is full. Ask Dave to open a
        // seat." names the person who can fix it, which is the only thing
        // the reader can act on.
        tell(
          messageOf(
            reply,
            'That did not work. Try again.',
            'Could not reach the site. You have not joined yet.',
          ),
          'bad',
        );
        return;
      }
      // The hub is now wrong by one league, and the switcher reads it too.
      leagues.reload();
      play('win');
      tell(
        helping && team
          ? `You help run ${team.name} now.`
          : team
            ? `You run ${teamName.trim()} now.`
            : `You're in. ${
                reply.data.draftAt
                  ? `Draft ${when(reply.data.draftAt, zone).replace('drafts ', 'is ')}.`
                  : 'Draft not scheduled yet.'
              }`,
        'good',
      );
      navigate(`/l/${reply.data.id}`);
    })();
  };

  return (
    <Wizard
      title={helping ? 'Help run a team' : team ? 'Take over a team' : 'Join a league'}
      steps={steps}
      index={index}
      onIndex={(next) => {
        setTouched(true);
        setIndex(next);
      }}
      onDone={join}
      onCancel={() => navigate('/')}
    />
  );
}
